import React, { useContext, useState, useCallback, useEffect, useMemo } from 'react';
import produce, { Draft } from 'immer';
import { useNavigate } from 'react-router-dom';
import { modeUIContext, showSuccessNotification, showDialog, DialogType } from '@moderepo/mode-ui-state';
import { ritzDataContext, setLoginInfo } from '@moderepo/mode-data-state-ritz';
import { capitalizeAll, getErrorMessage, ResetPasswordForm, setLocalStorageItem, useQueryParams } from '@moderepo/mode-app-components';
import { ApiError } from '@moderepo/mode-apis';
import { DEFAULT_ORGANIZATION_ID, RitzAPI } from '@moderepo/mode-internal-apis';
import { useRitzTranslation } from '../../utils/customHooks';
import { NotAuthorized, RouteHelper } from '..';
import * as Constants from '../../utils/constants';
import { LanguageSelectorContainer } from '../language-selector/LanguageSelectorContainer';
import { genericValidatePassword } from '../../utils/utils';
import { getOrganization } from '../app/App';


interface InputErrors {
    newPassword?: string | undefined;
    confirmNewPassword?: string |undefined;
    otherError?: string | undefined;
}


/**
 * Reset password page. This page is used for resetting an agent's password. This page should only be loaded
 * from clicking on a Reset Password email. To enter this page, the URL must has a 'token'.
 *
 * @param param
 */
export const ResetPassword: React.FC = () => {
    const navigate = useNavigate();
    const queryParams = useQueryParams();

    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { dispatch:dataDispatch } = useContext(ritzDataContext);
    const { trans } = useRitzTranslation(undefined);
    const [inputErrors, setInputErrors] = useState<InputErrors>({ });
    const routeHelper = RouteHelper.getInstance(navigate);
    const token = queryParams.get('token');

    // Get the organization and then get the password policy from the organization
    const organization = getOrganization();
    const passwordPolicy = useMemo(() => {
        return organization?.authSchemes.passwordPolicy;
    }, [organization]);



    /**
     * Once we have the organization, check if it has "passwordPolicy". If it DOES NOT, we need to take the user to the
     * not authorized page. Because this page is for resetting Password therefore "passwordPolicy" is required.
     */
    useEffect(() => {
        if (organization && !passwordPolicy) {
            routeHelper.gotoNotAuthorizedPage();
        }
    }, [organization, passwordPolicy, routeHelper]);


    /**
     * Submit reset password form
     */
    const onFormSubmit = useCallback(async (inputs: {
        readonly newPassword: string | undefined;
        readonly confirmNewPassword: String | undefined;
    }) => {

        // This shouldn't happen since the user should already be redirected to Not Authorized page. We just need an "undefined" check
        // here so that we don't have to do if in multiple places
        if (!organization || !passwordPolicy) {
            return false;
        }

        const { newPassword, confirmNewPassword } = inputs;
        const newInputErrors: InputErrors = { };

        // Validate newPassword and confirmPassword and make sure the password and confirm password are entered and they are the same
        newInputErrors.newPassword = genericValidatePassword(newPassword, passwordPolicy);

        if (!confirmNewPassword || confirmNewPassword.length <= 0) {
            newInputErrors.confirmNewPassword = 'error.input.confirm_password.empty';
        }

        if (newPassword && confirmNewPassword && newPassword !== confirmNewPassword) {
            newInputErrors.confirmNewPassword = 'error.input.confirm_password.not_match';
        }

        setInputErrors(newInputErrors);


        if (token && !newInputErrors.newPassword && !newInputErrors.confirmNewPassword && newPassword && confirmNewPassword) {
            // password and confirm password passed validation, submit reset password now

            try {
                // Verify account API call will also log the agent in so we can treat the agent as logged in
                // after the call is successful.
                const loginInfo = await RitzAPI.getInstance().resetPassword(organization.id, token, newPassword);
                setLocalStorageItem(Constants.LS_KEY_LOGIN_INFO, loginInfo);
                dataDispatch(setLoginInfo(loginInfo));

                if (loginInfo && loginInfo.authenticated && loginInfo.agentId && loginInfo.xsrfGuard) {
                    // Do nothing, the user will be taken to the home page automatically when login info is added to the state
                } else if (loginInfo && !loginInfo.authenticated && loginInfo.auth2FToken) {
                    // Take the agent to the login page
                    uiDispatch(showSuccessNotification('notif.password_reset_successfully'));
                    routeHelper.gotoLoginPage(true);
                }
            } catch (error) {
                setInputErrors({
                    ...inputErrors,
                    otherError: getErrorMessage((error as ApiError).code),
                });
            }
        }

        return false;
    }, [organization, passwordPolicy, token, dataDispatch, uiDispatch, routeHelper, inputErrors]);


    // Check the URL params for token. If token is not in the URL, redirect the agent to login page
    if (!token) {
        // Missing 'token' in the URL params, need to take agent to login page
        uiDispatch(showDialog({
            type          : DialogType.ERROR,
            title         : 'label.error',
            message       : 'notif.invalid_reset_password_token',
            positiveButton: 'label.ok',
        }));
        routeHelper.gotoLoginPage(true);

        // Return empty page
        return (
            <></>
        );
    }


    // If organization is undefined then that mean we weren't able to get organization based on baseUrl or orgId in the params
    if (!organization) {
        return <NotAuthorized />;
    }

    return (
        <ResetPasswordForm
            texts={{
                title   : trans('reset_password_form.title', { org_name: organization.name }),
                subtitle: organization.id === DEFAULT_ORGANIZATION_ID
                    ? trans('app.title_default')
                    : trans('app.title', { org_name: organization.name }),
                resetPasswordMessage        : trans('reset_password_form.message'),
                newPasswordInputLabel       : capitalizeAll(trans('label.password')),
                confirmNewPasswordInputLabel: capitalizeAll(trans('label.confirm_password')),
                loginButtonLabel            : trans('label.login'),
                resetPasswordButtonLabel    : trans('label.set_password'),
            }}
            errorTexts={{
                newPassword       : trans(inputErrors.newPassword || '', { min_len: passwordPolicy?.minLength }),
                confirmNewPassword: trans(inputErrors.confirmNewPassword || ''),
                otherError        : inputErrors.otherError ? trans(inputErrors.otherError) : '',
            }}
            languageSelectorComponent={<LanguageSelectorContainer projectId={undefined} minimal />}
            onGotoLogin={() => {
                routeHelper.gotoLoginPage();
            }}
            onFormInputChange={(field: string) => {
                setInputErrors(produce(inputErrors, (draft: Draft<InputErrors>) => {
                    draft[field] = undefined;
                    draft.otherError = undefined;
                }));
            }}
            onFormSubmit={onFormSubmit}
        />
    );

};
